import {
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { KeyboardArrowDown } from "@mui/icons-material";
import { useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import ReadOnlyTooltip from "components/common/ReadOnlyTooltip";
import PaginationControls from "components/fileStorage/PaginationControls";
import CollectionDialogs, {
  type CollectionAction,
} from "components/secureExchange/CollectionDialogs";
import ListToolbar from "components/secureExchange/ListToolbar";
import ProjectDialogs, {
  type ProjectAction,
} from "components/secureExchange/ProjectDialogs";
import ProjectStatusChip from "components/secureExchange/ProjectStatusChip";
import { DANGER_MENU_ITEM_SX } from "components/secureExchange/RowMenu";
import SecureExchangeBreadcrumbs from "components/secureExchange/SecureExchangeBreadcrumbs";
import SecureExchangeHeader from "components/secureExchange/SecureExchangeHeader";
import SecureExchangeShell from "components/secureExchange/SecureExchangeShell";
import SecureFileTable from "components/secureExchange/SecureFileTable";
import SecureUploadDialog from "components/secureExchange/SecureUploadDialog";
import { ProjectIcon } from "components/secureExchange/icons";
import { useDebouncedValue } from "components/secureExchange/useDebouncedValue";
import { useFileActions } from "components/secureExchange/useFileActions";
import { manageProjectItems } from "components/secureExchange/projectModel";
import { useSecureExchangePaths } from "components/secureExchange/useSecureExchangePaths";
import {
  type SecureProject,
  useGetSecureProjectItemsQuery,
  useGetSecureProjectQuery,
} from "features/secureUploads/secureUploads";
import { usePermissions } from "hooks/usePermissions";
import { useViewAs } from "hooks/useViewAs";
import { ACTIONS } from "utils/access";

const PAGE_SIZE = 25;

function ManageProject({
  project,
  readOnly,
  onAction,
}: {
  project: SecureProject;
  readOnly: boolean;
  onAction: (action: ProjectAction) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  return (
    <>
      <Button
        variant="outlined"
        endIcon={<KeyboardArrowDown />}
        onClick={(event) => setAnchor(event.currentTarget)}
      >
        Manage Project
      </Button>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        {manageProjectItems(project).flatMap((item) => [
          ...(item.kind === "delete" ? [<Divider key="line" />] : []),
          <MenuItem
            key={item.kind}
            disabled={readOnly}
            onClick={() => {
              setAnchor(null);
              onAction({ kind: item.kind, project });
            }}
            sx={{
              fontSize: 13,
              ...(item.kind === "delete" ? DANGER_MENU_ITEM_SX : {}),
            }}
          >
            {item.label}
          </MenuItem>,
        ])}
      </Menu>
    </>
  );
}

/** A project's page: its collections and the files at its root. */
export default function SecureExchangeProjectPage() {
  const params = useParams<{ clientId: string; projectId: string }>();
  const clientId = Number(params.clientId);
  const projectId = Number(params.projectId);
  const navigate = useNavigate();
  const paths = useSecureExchangePaths();
  const { readOnly } = useViewAs();
  const { sees } = usePermissions();
  const canOrganize = sees(ACTIONS.SECURE_EXCHANGE_ORGANIZE);
  const fileActions = useFileActions();
  const [projectAction, setProjectAction] = useState<ProjectAction | null>(
    null,
  );
  const [collectionAction, setCollectionAction] =
    useState<CollectionAction | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.trim());
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [uploadOpen, setUploadOpen] = useState(false);

  const {
    data: project,
    isLoading,
    isError,
  } = useGetSecureProjectQuery(projectId, { skip: !projectId });
  const {
    data: items,
    isLoading: loadingItems,
    isFetching,
  } = useGetSecureProjectItemsQuery(
    { id: projectId, search: search || undefined, page, page_size: limit },
    { skip: !projectId || isError },
  );
  const missing = !projectId || isError;
  const open = project?.status === "open";

  // An address with another client than the project's goes to the right one.
  if (project && project.client_id !== clientId)
    return (
      <Navigate replace to={paths.project(project.client_id, project.id)} />
    );

  // Anyone on the project uploads while it is open.
  const upload = open ? (
    <ReadOnlyTooltip active={readOnly}>
      <Button
        variant="contained"
        disabled={readOnly}
        onClick={() => setUploadOpen(true)}
      >
        Upload New File
      </Button>
    </ReadOnlyTooltip>
  ) : null;

  const newCollection =
    canOrganize && open ? (
      <ReadOnlyTooltip active={readOnly}>
        <Button
          variant="outlined"
          disabled={readOnly}
          onClick={() => setCollectionAction({ kind: "create", projectId })}
        >
          New Collection
        </Button>
      </ReadOnlyTooltip>
    ) : null;

  const total = items?.count ?? 0;
  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    <SecureExchangeShell
      title={project ? `${project.name} - Secure Exchange` : "Secure Exchange"}
      breadcrumbs={
        <SecureExchangeBreadcrumbs
          items={[
            paths.home,
            {
              label: project?.client_name ?? "",
              to: clientId ? paths.client(clientId) : undefined,
            },
            { label: project?.name ?? "" },
          ]}
        />
      }
      header={
        <SecureExchangeHeader
          icon={<ProjectIcon />}
          title={missing ? "Project not found" : project?.name}
          badge={project ? <ProjectStatusChip status={project.status} /> : null}
          subtitle={project?.description || undefined}
          loading={isLoading}
          actions={
            project && canOrganize ? (
              <ManageProject
                project={project}
                readOnly={readOnly}
                onAction={setProjectAction}
              />
            ) : null
          }
        />
      }
    >
      {missing ? (
        <Typography sx={{ p: 3 }} color="text.secondary">
          This project is not in your Secure Exchange.
        </Typography>
      ) : (
        <>
          <ListToolbar
            search={searchInput}
            onSearch={(value) => {
              setSearchInput(value);
              setPage(1);
            }}
            placeholder="Search this project"
            actions={
              <>
                {newCollection}
                {upload}
              </>
            }
          />
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              opacity: isFetching && !loadingItems ? 0.6 : 1,
            }}
          >
            <SecureFileTable
              files={items?.files ?? []}
              collections={items?.collections ?? []}
              collectionPath={(collection) =>
                paths.collection(clientId, projectId, collection.id)
              }
              collectionMenu={(collection) =>
                canOrganize && open
                  ? [
                      {
                        key: "rename",
                        label: "Rename Collection",
                        readOnly,
                        onClick: () =>
                          setCollectionAction({ kind: "rename", collection }),
                      },
                      {
                        key: "delete",
                        label: "Delete Collection",
                        danger: true,
                        readOnly,
                        onClick: () =>
                          setCollectionAction({ kind: "delete", collection }),
                      },
                    ]
                  : []
              }
              showCollection={Boolean(search)}
              loading={loadingItems}
              sort={null}
              onSort={() => undefined}
              empty={
                search
                  ? "Nothing in this project matches."
                  : "This project has no collections or files yet."
              }
              readOnly={readOnly}
              canOrganize={canOrganize}
              onAction={fileActions.run}
            />
          </Box>
          {total > 0 ? (
            <PaginationControls
              currentLimit={limit}
              onLimitChange={(next) => {
                setLimit(next);
                setPage(1);
              }}
              totalItems={total}
              currentPage={page}
              totalPages={pages}
              onPageChange={setPage}
              size="small"
              hasNextPage={page < pages}
            />
          ) : null}
        </>
      )}
      <ProjectDialogs
        action={projectAction}
        onClose={() => setProjectAction(null)}
        onDeleted={() => navigate(paths.client(clientId), { replace: true })}
      />
      <CollectionDialogs
        action={collectionAction}
        onClose={() => setCollectionAction(null)}
      />
      {fileActions.dialogs}
      {project ? (
        <SecureUploadDialog
          open={uploadOpen}
          onClose={() => setUploadOpen(false)}
          clientId={project.client_id}
          clientName={project.client_name}
          place={{ projectId: project.id, projectName: project.name }}
        />
      ) : null}
    </SecureExchangeShell>
  );
}
